/**
 * Page Scroll Command
 *
 * Scrolls the active (or a specified) window's content.
 * Triggered by the `page_scroll` tool via broadcastCmd → pendingCmds Y.Doc → commandRegistry.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import useSpaceStore from '../../stores/spaceStore';

/** Walk a window element's subtree to find its primary scrollable child. */
function findScrollableElement(root: Element): Element {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode() as Element | null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = walker.nextNode() as Element | null;
  }
  return root;
}

const pageScrollCommand: SelfContainedCommand = {
  id: 'page_scroll',
  description: 'Scroll the content of an open window or panel in the app',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      { name: 'direction', type: 'string', description: '"up", "down", "top", or "bottom"', required: true },
      { name: 'amount',    type: 'number', description: 'Pixels to scroll (up/down only). Default 400.', required: false },
      { name: 'windowId', type: 'string', description: 'Optional window ID to target. Omit for active window.', required: false },
    ],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { direction, amount = 400, windowId } = params ?? {};

    if (!direction) return createErrorResult('direction is required', 'direction is required', 'validation');

    // Resolve the target window element.
    const targetId: string | undefined =
      windowId ?? useSpaceStore.getState().activeWindowId ?? undefined;

    let windowEl: Element | null = targetId
      ? document.querySelector(`[data-window-id="${targetId}"]`)
      : null;

    // Last resort: any visible window on screen
    if (!windowEl) {
      windowEl = document.querySelector('[data-window-id]');
    }

    if (!windowEl) return createErrorResult('No scrollable window found', 'No scrollable window found');

    const container = findScrollableElement(windowEl);

    if (direction === 'top') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (direction === 'bottom') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      container.scrollBy({ top: direction === 'down' ? amount : -amount, behavior: 'smooth' });
    }

    return createSuccessResult(`Scrolled ${direction}`);
  },
} as SelfContainedCommand;

export const pageScrollCommands: SelfContainedCommand[] = [pageScrollCommand];
